import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";

import { useFirebaseAuth } from "../../firebase/AuthProvider";

import HomeScreen from "../HomeScreen";
import HistoryScreen from "../HistoryScreen";
import Text from "../../components/Text";
import { colors, fonts } from "../../theme";
import StartVisitDialog from "./StartVisitDialog";

type Tab = "upcoming" | "previous";

interface Props {
  navigation: any;
}

const CustomerDashboardWeb: React.FC<Props> = ({ navigation }) => {
  const [activeTab, setActiveTab] = React.useState<Tab>("upcoming");
  const [showStartVisit, setShowStartVisit] = React.useState(false);
  const [showAccount, setShowAccount] = React.useState(false);
  const { currentCustomer } = useFirebaseAuth();
  const firstName =
    currentCustomer?.firstName ||
    currentCustomer?.name?.split(" ")[0] ||
    "there";
  const initials = (currentCustomer?.name || firstName)
    .split(" ")
    .map((part: string) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <View style={styles.container}>
      <StartVisitDialog
        isOpen={showStartVisit}
        navigation={navigation}
        onClose={() => setShowStartVisit(false)}
      />

      <View style={styles.dashboardHeader}>
        <View>
          <Text style={styles.kicker}>CUSTOMER DASHBOARD</Text>
          <Text variant="bold" style={styles.dashboardTitle}>
            Hello, {firstName}
          </Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => setShowStartVisit(true)}
          style={styles.startButton}
        >
          <Text variant="bold" style={styles.startButtonText}>
            + New Visit
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => setShowAccount(!showAccount)}
          style={styles.avatar}
        >
          <Text variant="bold" style={styles.avatarText}>
            {initials}
          </Text>
        </TouchableOpacity>
        {showAccount && (
          <View style={styles.accountMenu}>
            <Text variant="bold" style={styles.accountName}>
              {currentCustomer?.name || "My account"}
            </Text>
            <Text numberOfLines={1} style={styles.accountEmail}>
              {currentCustomer?.emailAddress}
            </Text>
            <TouchableOpacity
              onPress={() => {
                setShowAccount(false);
                navigation.navigate("ViewProfile", {
                  screen: "CustomerProfile",
                  params: { customerId: currentCustomer?.id },
                });
              }}
              style={styles.menuItem}
            >
              <Text>Profile</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      <View style={styles.tabBar}>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityState={{ selected: activeTab === "upcoming" }}
          onPress={() => setActiveTab("upcoming")}
          style={[styles.tab, activeTab === "upcoming" && styles.tabActive]}
        >
          <Text
            style={[
              styles.tabText,
              activeTab === "upcoming" && styles.tabTextActive,
            ]}
          >
            Upcoming
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityState={{ selected: activeTab === "previous" }}
          onPress={() => setActiveTab("previous")}
          style={[styles.tab, activeTab === "previous" && styles.tabActive]}
        >
          <Text
            style={[
              styles.tabText,
              activeTab === "previous" && styles.tabTextActive,
            ]}
          >
            Previous
          </Text>
        </TouchableOpacity>
      </View>
      <View style={styles.content}>
        {activeTab === "upcoming" ? (
          <HomeScreen
            dashboardMode
            navigation={navigation}
            route={{ params: undefined } as any}
          />
        ) : (
          <HistoryScreen />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.black,
    flex: 1,
  },
  dashboardHeader: {
    alignItems: "center",
    backgroundColor: colors.black,
    borderBottomColor: colors.darkestGrey,
    borderBottomWidth: 1,
    flexDirection: "row",
    minHeight: 82,
    paddingHorizontal: 32,
    paddingVertical: 14,
    position: "relative",
    zIndex: 3,
  },
  dashboardTitle: {
    color: colors.white,
    fontSize: 24,
    marginTop: 2,
  },
  startButton: {
    backgroundColor: colors.yellow,
    borderRadius: 24,
    marginLeft: "auto",
    paddingHorizontal: 22,
    paddingVertical: 12,
  },
  startButtonText: {
    color: colors.black,
    fontSize: 12,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  content: {
    flex: 1,
    width: "100%",
  },
  tabBar: {
    alignSelf: "center",
    backgroundColor: colors.darkestGrey,
    borderRadius: 9,
    flexDirection: "row",
    marginBottom: 0,
    marginTop: 20,
    maxWidth: 420,
    padding: 4,
    width: "90%",
  },
  tab: {
    alignItems: "center",
    flex: 1,
    borderRadius: 7,
    justifyContent: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  tabText: {
    color: colors.darkGrey,
    fontFamily: fonts.semiBold,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  tabActive: {
    backgroundColor: colors.white,
  },
  tabTextActive: {
    color: colors.black,
  },
  kicker: {
    color: colors.darkGrey,
    fontSize: 10,
    letterSpacing: 1.5,
  },
  avatar: {
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 20,
    height: 40,
    justifyContent: "center",
    marginLeft: 12,
    width: 40,
  },
  avatarText: { color: colors.black, fontSize: 13 },
  accountMenu: {
    backgroundColor: colors.white,
    borderRadius: 10,
    padding: 14,
    position: "absolute",
    right: 24,
    shadowColor: colors.black,
    shadowOpacity: 0.2,
    shadowRadius: 12,
    top: 64,
    width: 230,
  },
  accountName: { color: colors.black, fontSize: 15 },
  accountEmail: {
    color: colors.midGrey,
    fontSize: 12,
    marginBottom: 10,
    marginTop: 2,
  },
  menuItem: {
    borderTopColor: colors.lightGrey,
    borderTopWidth: 1,
    paddingVertical: 10,
  },
});

export default CustomerDashboardWeb;
